import { useEffect, useRef } from 'react';

/**
 * Synchronise le scroll horizontal entre plusieurs éléments
 * @param {React.RefObject<HTMLElement>[] | HTMLElement[]} refsOrElements - Tableau de refs ou d'éléments à synchroniser
 * @returns {void}
 */
export function useSyncHorizontalScroll(refsOrElements) {
  const isSyncingRef = useRef(false);
  const handlersRef = useRef(new Map());
  const desiredScrollLeftRef = useRef(/** @type {number | null} */ (null));

  useEffect(() => {
    // Extraire les éléments (soit depuis refs, soit directement)
    const elements = refsOrElements
      .map((refOrElement) => {
        // Si c'est un objet avec .current, c'est une ref
        if (refOrElement && typeof refOrElement === 'object' && 'current' in refOrElement) {
          return refOrElement.current;
        }
        // Sinon, c'est directement un élément
        return refOrElement;
      })
      .filter((el) => el !== null && el !== undefined && el instanceof HTMLElement);

    if (elements.length < 2) {
      if (elements.length === 0) {
        desiredScrollLeftRef.current = null;
      }
      return undefined;
    }

    if (desiredScrollLeftRef.current === null) {
      desiredScrollLeftRef.current = elements.reduce(
        (max, el) => Math.max(max, el.scrollLeft),
        0,
      );
    }

    const referenceScrollLeft = desiredScrollLeftRef.current;

    elements.forEach((el) => {
      if (Math.abs(el.scrollLeft - referenceScrollLeft) > 0.5) {
        // eslint-disable-next-line no-param-reassign
        el.scrollLeft = referenceScrollLeft;
      }
    });

    // Nettoyer les anciens handlers pour les éléments qui ne sont plus dans la liste
    handlersRef.current.forEach((handler, el) => {
      if (!elements.includes(el)) {
        el.removeEventListener('scroll', handler);
        handlersRef.current.delete(el);
      }
    });

    /**
     * Crée un handler de scroll pour un élément donné
     * @param {HTMLElement} sourceElement - L'élément qui déclenche le scroll
     * @returns {() => void} Handler de scroll
     */
    const createScrollHandler = (sourceElement) => () => {
      // Si on est déjà en train de synchroniser, ignorer
      if (isSyncingRef.current) return;
      const { scrollLeft } = sourceElement;

      desiredScrollLeftRef.current = scrollLeft;

      // Marquer qu'on est en train de synchroniser
      isSyncingRef.current = true;

      // Synchroniser tous les autres éléments
      elements.forEach((targetElement) => {
        if (targetElement !== sourceElement && targetElement instanceof HTMLElement) {
          // Vérifier que la valeur est différente avant de modifier (évite les boucles)
          if (Math.abs(targetElement.scrollLeft - scrollLeft) > 0.5) {
            // eslint-disable-next-line no-param-reassign
            targetElement.scrollLeft = scrollLeft;
          }
        }
      });

      // Réinitialiser le flag après le frame
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    };

    // Créer et attacher les handlers pour les nouveaux éléments
    elements.forEach((el) => {
      if (el instanceof HTMLElement && !handlersRef.current.has(el)) {
        const handler = createScrollHandler(el);
        handlersRef.current.set(el, handler);
        el.addEventListener('scroll', handler, { passive: true });
      }
    });

    // Cleanup
    return () => {
      handlersRef.current.forEach((handler, el) => {
        el.removeEventListener('scroll', handler);
      });
      handlersRef.current.clear();
      isSyncingRef.current = false;
    };
  }, [refsOrElements]);
}

export default useSyncHorizontalScroll;
